import React from 'react';
import {
  DELIVERY_DEFINITION_OF_DONE,
  DELIVERY_LIFECYCLE_BANDS,
  DELIVERY_DOD_HEADER,
  DELIVERY_DOD_DESC,
} from '../../../content/enterpriseMessaging';

/**
 * Definition of Done as band lanes (Lilian spec, Option B "card timeline", household-tracker PR #55).
 * Content is the pre-#26 copy restored from c0bd0d0; only the visuals changed.
 * One card per lifecycle band. Multi-state bands span two columns with their states side by side;
 * UAT is the navy callout (the client's sign-off point).
 */

const STATE_INDEX = DELIVERY_DEFINITION_OF_DONE.reduce((acc, row, i) => ({ ...acc, [row.state]: i + 1 }), {});
const SIGN_OFF_STATE = 'UAT';
const { labels } = DELIVERY_DOD_HEADER;

/**
 * Lanes (Lilian #40 FIX 1: no row may hold a lone card).
 * <768: 1 col. >=768: one 6-track grid -> D+Planning / Build+Verification / Release+Live+Archive
 * (same 3-lane layout at every width from 768 up; no 1280+ override, Lilian #40 design call).
 */
const BAND_SPANS = [
  'md:col-span-2',
  'md:col-span-4',
  'md:col-span-2',
  'md:col-span-4',
  'md:col-span-2',
  'md:col-span-2',
  'md:col-span-2',
];
function bandSpan(band, i) {
  return BAND_SPANS[i] || 'md:col-span-2';
}

function StateCard({ row, n, inset }) {
  const navy = row.state === SIGN_OFF_STATE;
  return (
    <li
      data-testid="dod-step"
      data-stage={n}
      data-signoff={navy ? 'true' : undefined}
      className={`dod-state flex min-w-0 flex-col rounded-xl ${
        navy ? 'bg-logo-dark-blue text-white shadow-lg' : inset ? 'bg-brand-50' : ''
      } ${inset || navy ? 'p-fluid-stack' : ''}`}
    >
      <span className="sr-only">{`${labels.state} ${n} of ${DELIVERY_DEFINITION_OF_DONE.length}, ${row.state}.`}</span>
      <div className="flex items-center gap-3" aria-hidden="true">
        <span
          className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 font-display text-fluid-caption font-semibold ${
            navy ? 'bg-white text-logo-dark-blue' : 'bg-logo-dark-blue text-white'
          }`}
        >
          {n}
        </span>
        <h4 className={`font-display text-fluid-h3 font-semibold ${navy ? 'text-white' : 'text-ink-900'}`}>
          {row.state}
        </h4>
      </div>
      <dl className="dod-dl mt-3">
        <div>
          <dt className={`text-fluid-caption font-semibold uppercase tracking-[0.08em] ${navy ? 'text-ink-200' : 'text-ink-600'}`}>
            {labels.owner}
          </dt>
          <dd className={`mt-1 text-fluid-small ${navy ? 'text-white' : 'text-ink-600'}`}>{row.owner}</dd>
        </div>
        <div>
          <dt className={`text-fluid-caption font-semibold uppercase tracking-[0.08em] ${navy ? 'text-ink-200' : 'text-ink-600'}`}>
            {labels.criteria}
          </dt>
          <dd
            className={`mt-1 border-l-[3px] pl-3 text-fluid-small font-medium ${
              navy ? 'border-logo-light-blue text-white' : 'border-brand-700 text-ink-900'
            }`}
          >
            {row.exit}
          </dd>
        </div>
      </dl>
    </li>
  );
}

export default function DodFlow() {
  return (
    <figure aria-describedby="dod-desc" className="mt-fluid-gap" data-testid="dod-flow">
      <p id="dod-desc" className="sr-only">{DELIVERY_DOD_DESC}</p>
      <ol className="grid grid-cols-1 gap-fluid-gap md:grid-cols-6" data-testid="dod-groups">
        {DELIVERY_LIFECYCLE_BANDS.map((b, bi) => {
          const rows = b.states.map((s) => DELIVERY_DEFINITION_OF_DONE.find((r) => r.state === s));
          const multi = rows.length > 1;
          return (
            <li
              key={b.band}
              data-testid="dod-band"
              style={{ '--dod-i': bi }}
              className={`dod-rise card-hover relative min-w-0 overflow-hidden p-fluid-card motion-safe:hover:-translate-y-0.5 ${bandSpan(b, bi)}`}
            >
              <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-brand-500" />
              <h3 className="flex flex-wrap items-center gap-2 text-fluid-small">
                <span className="badge-navy !text-fluid-small">{b.band}</span>
                <span className="sr-only">{`, ${b.states.join(' and ')}`}</span>
                <span aria-hidden="true" className="text-fluid-caption font-medium text-ink-600">
                  {b.states.join(' · ')}
                </span>
              </h3>
              <ol className={`mt-fluid-stack grid grid-cols-1 gap-fluid-stack ${multi ? 'sm:grid-cols-2' : ''}`}>
                {rows.map((row) => (
                  <StateCard key={row.state} row={row} n={STATE_INDEX[row.state]} inset={multi} />
                ))}
              </ol>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}
